import { Component, OnInit, Pipe, PipeTransform, Input } from '@angular/core';
import { ToasterService } from 'angular5-toaster';
import { TicketOffer } from '../../core/ticketOffer';
import { TramApiService } from '../../tram-api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { UsingMode } from '../../core/review';
import { StorageService } from '../../storage.service';
import { FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { TicketService } from '../../providers/ticket.service';

@Component({
    selector: 'ticket-selector',
    templateUrl: './ticket-selector.component.html',
    styleUrls: ['./ticket-selector.component.scss']
})
export class TicketSelectorComponent implements OnInit {
    private _priceForm: FormGroup;
    private _buyForm: FormGroup;
    private totalCost: number;
     
    ticketOffers: TicketOffer[];

    price: number;
    maxBuyTripsCount: number;

    isLoading: boolean;
    totalPaymentText: string;
    currentTicketOffer: TicketOffer;
    canBuy: boolean;

    private _tripsCount: number;
    get tripsCount(): number {
        return this._tripsCount;
    }
    set tripsCount(value: number) {
        const tripsCount = typeof value === 'number'
            ? value
            : parseInt(value);

        if (this._tripsCount !== tripsCount) {
            this.onTripsCountChanged(tripsCount);
        }
    }

    @Input() theme = '';
    @Input() canEdit = false;

    get priceForm() { return this._priceForm; }
    get buyForm() { return this._buyForm; }

    UsingMode = UsingMode;
    mode: UsingMode = UsingMode.Anonymous;

    bonusForms: BonusForm[];

    isEditionMode: boolean;

    constructor(private api: TramApiService,
        private toaster: ToasterService,
        private storage: StorageService,
        private ticketService: TicketService) {
    }

    ngOnInit() {
        this.mode = UsingMode.Anonymous;

        if (this.canEdit) {
            this.tryInitGodMode();
        }

        this.load();
    }

    onNumberKeypress(event) {
        return (event.charCode === 8 || event.charCode === 0)
            ? null
            : event.charCode >= 48 && event.charCode <= 57;
    }
    onNumberInput(event) {
        if (isNaN(parseInt(event.target.value))) {
            event.target.value = '';
        }
    }

    enableEditionMode() {
        this.isEditionMode = true;

        this.bonusForms = [];

        this.ticketOffers.forEach((offer) => this.addForm(offer));        

        this._priceForm = new FormGroup({
            'price': new FormControl(this.price, Validators.required)
        });
        this._buyForm = new FormGroup({
            'maxCount': new FormControl(this.maxBuyTripsCount, Validators.required)
        });

        this.onChanges();
    }

    onChanges(): void {
        this._priceForm.valueChanges.subscribe(value => {
            if (this.price === value.price) return;

            this.price = value.price;
            this.updateForms();
        });
        this._buyForm.valueChanges.subscribe(value => {
            if (this.maxBuyTripsCount === value.maxCount) return;

            this.maxBuyTripsCount = value.maxCount;
        });
    }

    addForm(offer?: TicketOffer) {
        const bonusForm = new BonusForm(this.price, offer);
        this.bonusForms.push(bonusForm);
    }

    updateForms() {
        this.bonusForms.forEach((bonusForm, i) => {
            bonusForm.price = this.price;
        });
    }

    removeForm(bonusForm: BonusForm) {
        // remove new ticket offer which not exists in database
        if (!bonusForm.id) {
            const index = this.bonusForms.findIndex(b => b.id === bonusForm.id);

            if (index >= 0) {
                this.bonusForms.splice(index, 1);
            }

            return;
        }

        // remove ticket offer which exists in database
        this.ticketService
            .remove(bonusForm.id)
            .subscribe(isRemoved => {
                if (isRemoved) {
                    const index = this.bonusForms.findIndex(b => b.id === bonusForm.id);
                    const indexTicketOffer = this.ticketOffers.findIndex(o => o.id === bonusForm.id);

                    if (index >= 0) {
                        this.bonusForms.splice(index, 1);
                    }

                    if (indexTicketOffer >= 0) {
                        this.ticketOffers.splice(index, 1);
                    }

                    this.toaster.pop('success', null, 'Бонусное предложение удалено');
                }
            }, error => {
                this.toaster.pop('error', null, error.statusText);
            });
    }

    onSubmit() {
        const isValid = this.validateForms();
        if (!isValid) return;

        if (this.buyForm.dirty) {
            this.submitMaxBuyTripsCount();
        }

        if (this.priceForm.dirty) {
            this.submitPriceAndTicketOffers();
        } else {
            this.submitTicketOffers();   
        }
    }

    private submitMaxBuyTripsCount() {
        this.ticketService
            .updateMaxBuyTripsCount(this.maxBuyTripsCount)
            .subscribe(maxBuyTripsCount => {
                this.maxBuyTripsCount = maxBuyTripsCount;
                this.toaster.pop('success', null, 'Максимальное количество поездок за 1 покупку обновлено');
            }, error => {
                this.toaster.pop('error', null, error.statusText);
            });
    }

    private submitPriceAndTicketOffers() {
        this.ticketService
            .updateBasePrice(this.price)
            .subscribe(price => {
                this.price = price;
                this.toaster.pop('success', null, 'Стоимость поездки обновлена');

                this.submitTicketOffers();
            }, error => {
                this.toaster.pop('error', null, error.statusText);
            });
    }

    private submitTicketOffers() {
        for (let i = 0; i < this.bonusForms.length; i++) {
            const b = this.bonusForms[i];

            let isDirty = b.form.dirty;
            let serializedModel = {};

            const value = new Object(b.value);
            if (i === this.bonusForms.length - 1) {
                value['maxTripsCount'] = null;
                isDirty = true;
            } else if (i === 0) {
                value['minTripsCount'] = 1;
                isDirty = true;
            } 

            if (!isDirty) continue;

            if (!value['bonusTripsCount']) {
                value['bonusTripsCount'] = 0;
            }
            if (!value['discount']) {
                value['discount'] = 0;
            }
            serializedModel = JSON.stringify(value);

            if (!b.id) {
                // new bonus
                this.ticketService
                    .publish(serializedModel)
                    .subscribe(newTicketOffer => {
                        this.ticketOffers.splice(i, 0, newTicketOffer);
                        this.toaster.pop('success', null, 'Новое бонусное предложение сохранено');
                    }, error => {
                        this.toaster.pop('error', null, error.statusText);
                    });
            } else {
                // update bonuse
                this.ticketService
                    .update(b.id, serializedModel)
                    .subscribe(updatedTicketOffer => {
                        const indexTicketOffer = this.ticketOffers.findIndex(o => o.id === updatedTicketOffer.id);

                        if (indexTicketOffer >= 0) {
                            const ticketOffer = this.ticketOffers[indexTicketOffer];
                            ticketOffer.discount = updatedTicketOffer.discount;
                            ticketOffer.price = updatedTicketOffer.price;
                            ticketOffer.cost = updatedTicketOffer.cost;
                            ticketOffer.bonusTripsCount = updatedTicketOffer.bonusTripsCount;
                            ticketOffer.minTripsCount = updatedTicketOffer.minTripsCount;
                            ticketOffer.maxTripsCount = updatedTicketOffer.maxTripsCount;
                        }

                        this.toaster.pop('success', null, 'Бонусное предложение обновлено');
                    }, error => {
                        this.toaster.pop('error', null, error.statusText);
                    });
            }
        }
    }

    private load() {
        this.loadMaxBuyTripsCount();        
        this.loadPriceAndTicketOffers();
    }

    private loadMaxBuyTripsCount(): void {
        this.ticketService.getMaxBuyTripsCount()
            .subscribe(maxCount => this.maxBuyTripsCount = maxCount, 
            err => this.handleError(err));
    }

    private loadPriceAndTicketOffers(): void {
        this.isLoading = true;
        
        this.ticketService.getBasePrice()
            .subscribe(price => {
                this.price = price;

                this.ticketService.get()
                    .subscribe(ticketOffers => {
                        this.isLoading = false;
                        this.ticketOffers = ticketOffers;
                    }, err => this.handleError(err))
            }, 
            err => this.handleError(err));
    }

    private getTicketOfferByTripsCount(tripsCount: number): TicketOffer {
        return this.ticketOffers
            .find(t => {
                return tripsCount >= t.minTripsCount && (
                    !t.maxTripsCount || tripsCount <= t.maxTripsCount
                );
            });
    }

    private onTripsCountChanged(tripsCount: number) {
        if (isNaN(tripsCount)) {
            this._tripsCount = null;
            this.totalCost = null;
            this.currentTicketOffer = null;
            this.totalPaymentText = '';
            this.canBuy = false;
            return;
        }

        if (this.maxBuyTripsCount && tripsCount > this.maxBuyTripsCount) {
            this.toaster.pop('error', null, `Максимальное количество поездок - ${this.maxBuyTripsCount}`);
            this.canBuy = false;
            return;
        }

        this._tripsCount = tripsCount;

        this.currentTicketOffer = this.getTicketOfferByTripsCount(tripsCount);
        if (!this.currentTicketOffer) {
            this.totalPaymentText = '';
            this.toaster.pop('error', null, 'Нет доступных билетов');
            this.canBuy = false;
            return;
        }

        this.totalCost = tripsCount * this.currentTicketOffer.cost;

        const bonusTripsCount = this.currentTicketOffer.bonusTripsCount;
        const totalTripsCount = tripsCount + bonusTripsCount;

        this.totalPaymentText = [
            tripsCount,
            bonusTripsCount? '+ ' + bonusTripsCount : '',
            totalTripsCount === 1 ? 'поездку' : totalTripsCount < 5 ? 'поездки' : 'поездок',
            `за ${this.totalCost} руб.`
        ].join(' ');
        
        this.canBuy = true;
    }

    private handleError(error: HttpErrorResponse | string) {
        this.isLoading = false;
        if (error instanceof HttpErrorResponse) {
            this.toaster.pop('error', null, error.statusText);
        } else {
            this.toaster.pop('error', null, error);
        }
    }

    private tryInitGodMode() {
        if (this.storage.isSensitiveDataExist.value) {
            this.api
                .getCurrentUser()
                .then(user => {
                    this.mode = user && user.isAdmin
                        ? UsingMode.God
                        : UsingMode.Anonymous;
                })
                .catch(error => { });
        }
    }

    private validateForms(): boolean {
        let isInvalid = false;

        if (!this.price) {
            this.toaster.pop('error', null, 'Укажите корректную стоимость поездки');
            isInvalid = true;
        }

        // first validation step - check bonus count
        this.bonusForms.forEach((b, i) => {
            if (b.bonus < 0) {
                b.form.controls['bonusTripsCount'].setErrors({ 'incorrect': true });
                b.form.controls['bonusTripsCount'].markAsTouched();

                this.toaster.pop('error', null, 'Неверное число бонусных поездок');
                isInvalid = true;
            } else if (b.bonus > 0 && b.discount > 0) {
                b.form.controls['discount'].setErrors({ 'incorrect': true });
                b.form.controls['discount'].markAsTouched();

                isInvalid = true;

                this.toaster.pop('error', null, 'Бонусные поездки нельзя использовать вместе со скидкой на билет');
                isInvalid = true;
            } 
        });

        // second validation step - check min and max offer count
        let previousMaxOfferCount = -1;
        for (let i = 0; i < this.bonusForms.length; i++) {
            const b = this.bonusForms[i];

            if (b.minOfferCount <= b.maxOfferCount) {
                if (previousMaxOfferCount >= b.minOfferCount) {
                    b.form.controls['minTripsCount'].setErrors({ 'incorrect': true });
                    b.form.controls['minTripsCount'].markAsTouched();

                    isInvalid = true;
                }
            } else {
                // serve last bonus condition without specified value of max offer count
                if (i === this.bonusForms.length - 1) {
                    if (previousMaxOfferCount >= b.minOfferCount) {
                        b.form.controls['minTripsCount'].setErrors({ 'incorrect': true });
                        b.form.controls['minTripsCount'].markAsTouched();

                        isInvalid = true;
                    }
                } else {
                    b.form.controls['maxTripsCount'].setErrors({ 'incorrect': true });
                    b.form.controls['maxTripsCount'].markAsTouched();

                    isInvalid = true;
                }
            }

            previousMaxOfferCount = b.maxOfferCount;
        }

        return !isInvalid;
    }
}

class BonusForm {
    private _form: FormGroup;
    private _offer: TicketOffer;

    id: string;

    get form() { return this._form; }
    get bonus() { return this._offer.bonusTripsCount; }
    get minOfferCount() { return this._offer.minTripsCount; }
    get maxOfferCount() { return this._offer.maxTripsCount; }
    get discount() { return this._offer.discount; }

    set price(value: number) { 
        this._offer.price = value; 
        this._offer.cost = value * (1 - this.discount);
    }
    get price() { return this._offer.price; }
    get cost() { return this.price * (1 - this.discount); }
    get value() {
        const model = this._form.value;
        model['price'] = this.price;
        model['cost'] = this.cost;
        model['discount'] = this.discount;

        return model;
    }

    constructor(price: number, ticketOffer: TicketOffer = null) {
        if (ticketOffer) {
            this._offer = ticketOffer;          
            this.id = this._offer.id;
        } else {
            this._offer = new TicketOffer();
            this._offer.bonusTripsCount = 0;
            this._offer.minTripsCount = 0;
            this._offer.maxTripsCount = 0;
            this._offer.discount = 0;
        }

        this.price = price;

        this._form = new FormGroup({
            'bonusTripsCount': new FormControl(this._offer.bonusTripsCount, Validators.required),
            'minTripsCount': new FormControl(this._offer.minTripsCount, Validators.required),
            'maxTripsCount': new FormControl(this._offer.maxTripsCount, Validators.required),
            'discount': new FormControl(this._offer.discount * 100, Validators.required)
        });

        this.onChanges();
    }

    onChanges(): void {
        this._form.valueChanges.subscribe(value => {
            this._offer.bonusTripsCount = value.bonusTripsCount;
            this._offer.minTripsCount = value.minTripsCount;
            this._offer.maxTripsCount = value.maxTripsCount;
            this._offer.discount = value.discount / 100;
        });
    }
}



// WEBPACK FOOTER //
// D:/programming/Чижик_Api_Site_Sources-Electroline/front/src/app/common/ticket/ticket-selector.component.ts